import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './MetaTraderPlatform.css';
import realTimeDataService from './components/RealTimeDataService';

// ✅ EXTRACTED: The working RealTimeChart from your old code
const RealTimeChart = ({ symbol = 'FX:EURUSD', interval = '60' }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      
      const widgetDiv = document.createElement('div');
      widgetDiv.className = 'tradingview-widget-container__widget';
      widgetDiv.style.height = 'calc(100% - 32px)';
      widgetDiv.style.width = '100%';

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
      script.async = true;

      const widgetConfig = {
        autosize: true,
        symbol: symbol,
        interval: interval,
        timezone: 'Etc/UTC',
        theme: 'light',
        style: '1',
        locale: 'en',
        toolbar_bg: '#ffffff',
        enable_publishing: false,
        withdateranges: true,
        allow_symbol_change: false,
        save_image: false,
        calendar: false,
        hide_top_toolbar: false,
        hide_legend: false,
        hide_side_toolbar: false,
        details: true,
        hotlist: false,
        studies: ['Volume@tv-basicstudies'],
        show_popup_button: true,
        popup_width: '1000',
        popup_height: '650',
        overrides: {
          'paneProperties.background': '#ffffff',
          'paneProperties.vertGridProperties.color': '#f0f0f0',
          'paneProperties.horzGridProperties.color': '#f0f0f0',
          'symbolWatermarkProperties.transparency': 90,
          'scalesProperties.textColor': '#333333',
          'mainSeriesProperties.candleStyle.upColor': '#26a69a',
          'mainSeriesProperties.candleStyle.downColor': '#ef5350',
          'mainSeriesProperties.candleStyle.drawWick': true,
          'mainSeriesProperties.candleStyle.drawBorder': true,
          'mainSeriesProperties.candleStyle.borderUpColor': '#26a69a',
          'mainSeriesProperties.candleStyle.borderDownColor': '#ef5350',
          'mainSeriesProperties.candleStyle.wickUpColor': '#26a69a',
          'mainSeriesProperties.candleStyle.wickDownColor': '#ef5350',
          'volumePaneSize': 'medium'
        }
      };

      script.innerHTML = JSON.stringify(widgetConfig);
      containerRef.current.appendChild(widgetDiv);
      containerRef.current.appendChild(script);
    }
  }, [symbol, interval]);

  return (
    <div ref={containerRef} className="tradingview-widget-container" style={{ height: '100%', width: '100%', backgroundColor: 'white', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666', fontSize: '14px', flexDirection: 'column', gap: '10px' }}>
        <div className="loading-spinner"></div>
        <div>Loading {symbol.replace('TVC:', '').replace('FX:', '').replace('NASDAQ:', '')} chart...</div>
        <div style={{ fontSize: '12px', color: '#1976d2' }}>Professional MetaTrader 5 Charts</div>
      </div>
    </div>
  );
};
const getDisplayName = (symbol) => symbol === 'XAUUSD2' ? 'XAU-USD' : (symbol || 'N/A');
const getBottomPanelName = (symbol) => symbol === 'XAUUSD2' ? 'XAUUSD' : (symbol || 'N/A');
// ✅ SYMBOL MAPPING: Map your real API symbols to TradingView symbols
const getSymbolMapping = () => ({
  'XRPUSD': 'COINBASE:XRPUSD',
  'XAUUSD': 'TVC:GOLD',
  'EURUSD': 'FX:EURUSD',
  'GBPUSD': 'FX:GBPUSD',
  'BTCUSD': 'COINBASE:BTCUSD',
  'AAPL': 'NASDAQ:AAPL',
  'GOOGL': 'NASDAQ:GOOGL',
  'TSLA': 'NASDAQ:TSLA',
  'US500': 'SP:SPX',
  'ETHUSD': 'COINBASE:ETHUSD',
  'USDJPY': 'FX:USDJPY',
  'AUDUSD': 'FX:AUDUSD',
  'USDCAD': 'FX:USDCAD',
  'USDCHF': 'FX:USDCHF',
  'NZDUSD': 'FX:NZDUSD',
  'EURGBP': 'FX:EURGBP',
  'EURJPY': 'FX:EURJPY',
  'GBPJPY': 'FX:GBPJPY',
  'EURCHF': 'FX:EURCHF',
  'GBPCHF': 'FX:GBPCHF',
  'AUDCHF': 'FX:AUDCHF',
  'EURAUD': 'FX:EURAUD',
  'EURCAD': 'FX:EURCAD',
  'EURNZD': 'FX:EURNZD',
  'GBPAUD': 'FX:GBPAUD',
  'GBPCAD': 'FX:GBPCAD',
  'CADCHF': 'FX:CADCHF',
  'XAGUSD': 'TVC:SILVER',
  'XPDUSD': 'TVC:PALLADIUM',
  'XPTUSD': 'TVC:PLATINUM',
  'WTI': 'NYMEX:CL1!',
  'BRENT': 'TVC:UKOIL',
  'NATGAS': 'NYMEX:NG1!',
  'HEATING': 'NYMEX:HO1!',
  'GASOLINE': 'NYMEX:RB1!',
  'NAS100': 'NASDAQ:NDX',
  'US30': 'DJ:DJI',
  'GER40': 'XETR:DAX',
  'UK100': 'TVC:UKX',
  'FRA40': 'TVC:CAC',
  'JPN225': 'TVC:NI225',
  'AUS200': 'TVC:AS51',
  'LTCUSD': 'COINBASE:LTCUSD',
  'ADAUSD': 'COINBASE:ADAUSD',
  'DOTUSD': 'COINBASE:DOTUSD',
  'LINKUSD': 'COINBASE:LINKUSD',
  'BNBUSD': 'BINANCE:BNBUSD',
  'MSFT': 'NASDAQ:MSFT',
  'AMZN': 'NASDAQ:AMZN',
  'META': 'NASDAQ:META',
  'NVDA': 'NASDAQ:NVDA',
  'NFLX': 'NASDAQ:NFLX',
  'BABA': 'NYSE:BABA',
  'AMD': 'NASDAQ:AMD'
});

// ✅ ENHANCED: Trading calculations helper functions
const calculateCommission = (symbol, volume) => {
  if (symbol.includes('USD') && !symbol.includes('BTC') && !symbol.includes('ETH')) {
    return volume * 7;
  } else if (symbol.includes('BTC') || symbol.includes('ETH') || symbol.includes('XRP')) {
    return volume * 15;
  } else if (['AAPL', 'GOOGL', 'TSLA', 'MSFT', 'AMZN'].includes(symbol)) {
    return volume * 10;
  } else if (symbol.includes('XAU') || symbol.includes('XAG')) {
    return volume * 12;
  }
  return volume * 8;
};

const calculateSwap = (symbol, volume, type, hoursHeld) => {
  const swapRates = {
    'EURUSD': type === 'BUY' ? -2.1 : 0.3,
    'GBPUSD': type === 'BUY' ? -1.8 : -0.2,
    'USDJPY': type === 'BUY' ? -3.2 : 1.4,
    'BTCUSD': type === 'BUY' ? -8.5 : -4.2,
    'XRPUSD': type === 'BUY' ? -6.2 : -3.1,
    'XAUUSD': type === 'BUY' ? -4.1 : 1.8,
    'AAPL': type === 'BUY' ? -0.5 : -0.3,
    'TSLA': type === 'BUY' ? -1.2 : -0.8
  };
  
  const baseRate = swapRates[symbol] || (type === 'BUY' ? -1.5 : 0.5);
  const daysHeld = Math.floor(hoursHeld / 24);
  return baseRate * volume * daysHeld;
};

const calculateProfit = (openPrice, currentPrice, volume, isBuy, symbol) => {
  if (!openPrice || !currentPrice || openPrice === 0) return 0;

  const priceDiff = isBuy
    ? (currentPrice - openPrice)
    : (openPrice - currentPrice);

  const sym = symbol.toUpperCase();

  // ✅ Forex standard pairs (EURUSD, GBPUSD etc.) — 1 lot = 100,000 units
  // Profit = priceDiff × 100,000 × volume (already in USD for XXX/USD pairs)
  if (
    sym === 'EURUSD' || sym === 'GBPUSD' || sym === 'AUDUSD' ||
    sym === 'NZDUSD' || sym === 'USDCAD' || sym === 'USDCHF' ||
    sym.includes('EUR') || sym.includes('GBP') || sym.includes('AUD') ||
    sym.includes('CAD') || sym.includes('CHF') || sym.includes('NZD')
  ) {
    return priceDiff * 100000 * volume;
  }

  // ✅ JPY pairs — 1 lot = 100,000 units, profit in JPY → divide by current rate (~150)
  // Simplified: priceDiff × 100,000 × volume / 150
  if (sym.includes('JPY')) {
    return (priceDiff * 100000 * volume) / 150;
  }

  // ✅ Gold (XAUUSD) — 1 lot = 100 troy ounces
  // Profit = priceDiff × 100 × volume
  if (sym.includes('XAU') || sym.includes('GOLD')) {
    return priceDiff * 100 * volume;
  }

  // ✅ Silver (XAGUSD) — 1 lot = 5000 ounces
  if (sym.includes('XAG') || sym.includes('SILVER')) {
    return priceDiff * 5000 * volume;
  }

  // ✅ BTC, ETH, XRP, LTC etc — 1 lot = 1 coin
  // Profit = priceDiff × 1 × volume
  if (
    sym.includes('BTC') || sym.includes('ETH') || sym.includes('XRP') ||
    sym.includes('LTC') || sym.includes('ADA') || sym.includes('DOT') ||
    sym.includes('LINK') || sym.includes('BNB')
  ) {
    return priceDiff * 1 * volume;
  }

  // ✅ Oil/Energy — 1 lot = 1000 barrels
  if (sym.includes('WTI') || sym.includes('OIL') || sym.includes('BRENT') || sym.includes('NATGAS')) {
    return priceDiff * 1000 * volume;
  }

  // ✅ US Stock Indices
  if (sym.includes('US500') || sym.includes('SPX')) return priceDiff * 50 * volume;
  if (sym.includes('NAS100') || sym.includes('NDX')) return priceDiff * 20 * volume;
  if (sym.includes('US30') || sym.includes('DJI')) return priceDiff * 5 * volume;
  if (sym.includes('UK100')) return priceDiff * 10 * volume;
  if (sym.includes('GER40') || sym.includes('DAX')) return priceDiff * 25 * volume;

  // ✅ Individual Stocks (AAPL, TSLA etc.) — 1 lot = 100 shares
  if (
    sym === 'AAPL' || sym === 'GOOGL' || sym === 'TSLA' ||
    sym === 'MSFT' || sym === 'AMZN' || sym === 'META' ||
    sym === 'NVDA' || sym === 'NFLX' || sym === 'AMD' || sym === 'BABA'
  ) {
    return priceDiff * 100 * volume;
  }

  // ✅ Default fallback — standard forex
  return priceDiff * 100000 * volume;
};

// ✅ MAIN PLATFORM - With PERFECT Profit/Equity Logic
const MetaTraderPlatform = () => {
  const [activeTradingAccount, setActiveTradingAccount] = useState(null);
const [algoTradingActive, setAlgoTradingActive] = useState(false);
  const [persistentHistory, setPersistentHistory] = useState([]);
  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  });

const API_BASE_URL = "https://final-project-5ar7.onrender.com";
console.log('🌐 METATRADER API_BASE_URL:', API_BASE_URL);

// ✅ FIX #2: Fix auth headers (Line 203)  
const getAuthHeaders = () => {
  // ✅ FIXED: Check all possible token keys
  const token = localStorage.getItem('token') || 
                localStorage.getItem('userToken') || 
                localStorage.getItem('authToken');
  console.log('🔑 Using auth token:', token ? 'Token found' : 'No token found');
  console.log('🔑 Token preview:', token ? token.substring(0, 20) + '...' : 'None');
  
  if (!token) {
    console.error('❌ No authentication token found!');
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}
  const [selectedSymbol, setSelectedSymbol] = useState('XRPUSD');
  const [selectedCategory, setSelectedCategory] = useState('Most Popular');
  const [timeframe, setTimeframe] = useState('60');
  const [positions, setPositions] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
// ✅ NEW: Admin alerts + credit
const [adminAlerts, setAdminAlerts] = useState([]);
const [accountCredit, setAccountCredit] = useState(0);
const [unreadAlertCount, setUnreadAlertCount] = useState(0);
// ✅ NEW: SL/TP inline editing
const [editingSLTPId, setEditingSLTPId] = useState(null);
const [editSLValue, setEditSLValue] = useState('');
const [editTPValue, setEditTPValue] = useState('');
  const [activeTerminalTab, setActiveTerminalTab] = useState('Trade');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [isMarketWatchExpanded, setIsMarketWatchExpanded] = useState(true);
  const [liveQuotes, setLiveQuotes] = useState({});
  const [selectedVolume, setSelectedVolume] = useState(0.01);
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const navigate = useNavigate();

 const syncPositionsFromBackend = async () => {
  if (!isMountedRef.current) return; // ✅ Stop if unmounted
  try {
    const accountId = activeTradingAccount?.accountId;
    if (!accountId) {
      console.log('⚠️ No accountId available for syncing positions');
      return;
    }
    
    const response = await fetch(`${API_BASE_URL}/api/trading/open-positions/${accountId}`, {
      headers: getAuthHeaders()
    });

    if (response.ok) {
      const result = await response.json();

      // ✅ FIX: If backend returns negative equity positions, skip sync
      // This happens when a position was closed locally but backend hasn't updated yet
      if (result.account && result.account.equity < -100) {
        console.warn('⚠️ equity is very negative, skipping sync to prevent stale data override');
        return;
      }

      const backendPositions = result.positions.map(pos => ({
        id: pos.positionId,
        positionId: pos.positionId,
        symbol: pos.symbol,
        type: pos.type.toUpperCase(),
        volume: pos.volume,
        openPrice: pos.openPrice,
        currentPrice: pos.currentPrice || pos.openPrice,
        openTime: new Date(pos.openedAt).getTime(),
        time: new Date(pos.openedAt).toLocaleTimeString(),
        date: new Date(pos.openedAt).toLocaleDateString(),
        profit: pos.unrealizedPnL || 0,
        swap: pos.swap || 0,
        commission: -Math.abs(pos.commission || 0),
        stopLoss: 0,
        takeProfit: 0,
        lastUpdate: Date.now(),
        isBackendSynced: true
      }));
          const removedByAdmin = positions.filter(local =>
      !backendPositions.find(bp => bp.positionId === local.positionId)
    );
    if (removedByAdmin.length > 0) {
      console.log('Admin closed positions detected:', removedByAdmin.length);
      await loadBackendTradeHistory();
      setActiveTerminalTab('account-history');
    }
    setPositions(backendPositions);
      console.log('✅ Synced', backendPositions.length, 'positions from backend');
      
      // ✅ FIXED: Update equity AFTER positions are set
      if (activeTradingAccount && backendPositions.length > 0) {
        const totalUnrealizedPnL = backendPositions.reduce((sum, pos) => sum + (pos.profit || 0), 0);
        const newEquity = activeTradingAccount.balance + totalUnrealizedPnL;
        setActiveTradingAccount(prev => ({
          ...prev,
          equity: newEquity
        }));
        console.log('✅ Equity updated after sync:', newEquity.toFixed(2));
      }
    }
  } catch (error) {
    console.error('❌ Failed to sync positions:', error);
  }
};

  // ✅ NEW: Update position prices on backend
  const updatePositionOnBackend = useCallback(async (positionId, currentPrice) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/trading/update-position/${positionId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ currentPrice: parseFloat(currentPrice) })
      });

      if (response.ok) {
        console.log('✅ Position price updated on backend:', positionId, currentPrice);
      }
    } catch (error) {
      console.error('❌ Error updating position price:', error);
    }
  }, []);

 const fetchTradingAccountData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/public/user-trading-accounts/${user.id}`, {
      headers: getAuthHeaders()
    });
    const accounts = await response.json();
    
    if (accounts.length > 0) {
      // ✅ CRITICAL FIX: Always use the CURRENT logged-in account, never switch accounts
      const currentAccountId = activeTradingAccount?.accountId;
      const approvedAccount = currentAccountId
        ? accounts.find(acc => acc.accountId === currentAccountId)
        : accounts.find(acc => acc.adminStatus === 'approved') || accounts[0];

      if (!approvedAccount) {
        console.warn('⚠️ Current account not found in API response');
        return;
      }

      const balance  = parseFloat(approvedAccount.balance)  || 0;
      const credit   = parseFloat(approvedAccount.credit)   || 0;
      const leverage = parseInt(approvedAccount.leverage)   || 100;
      const group    = approvedAccount.group || 'STANDARD';

      // ✅ Equity = Balance + Credit (floating PnL added by 2s interval)
      const equity = balance + credit;
      const margin = parseFloat(approvedAccount.margin) || 0;
      const freeMargin = equity - margin;

      console.log('📊 Account refreshed — AccountId:', currentAccountId, 'Balance:', balance, 'Credit:', credit);

      setActiveTradingAccount(prev => ({
        ...prev,  // ✅ Keep existing accountId, never overwrite it
        balance,
        credit,
        equity,
        margin,
        freeMargin,
        leverage,
        group
        // ✅ DO NOT update accountId here — it was set correctly at login
      }));

      setAccountCredit(credit);
    }
  } catch (error) {
    console.error('❌ Failed to fetch trading account data:', error);
  }
};

  // ✅ UPDATED: Backend integration functions
  const openPositionOnBackend = async (type) => {
    try {
      const currentPrice = liveQuotes[selectedSymbol]?.price || 1.0000;
      
      console.log('🚀 Opening position on backend:', {
        accountId: activeTradingAccount.accountId,
        symbol: selectedSymbol,
        type: type.toLowerCase(),
        volume: parseFloat(selectedVolume),
        openPrice: type === 'BUY' ? liveQuotes[selectedSymbol]?.ask || currentPrice : liveQuotes[selectedSymbol]?.bid || currentPrice
      });

      const response = await fetch(`${API_BASE_URL}/api/trading/open-position`, {
        method: 'POST',
        headers: getAuthHeaders(), // ✅ FIXED: Add auth headers
        body: JSON.stringify({
          accountId: activeTradingAccount.accountId,
          symbol: selectedSymbol,
          type: type.toLowerCase(),
          volume: parseFloat(selectedVolume),
          openPrice: type === 'BUY' ? liveQuotes[selectedSymbol]?.ask || currentPrice : liveQuotes[selectedSymbol]?.bid || currentPrice
        })
      });

      console.log('📡 Backend response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('✅ Position opened on backend:', result.position.positionId);
          
          // Create position locally with backend data
          const backendPosition = {
            id: result.position.positionId,
            positionId: result.position.positionId,
            symbol: result.position.symbol,
            type: result.position.type.toUpperCase(),
            volume: result.position.volume,
           openPrice: parseFloat(parseFloat(result.position.openPrice).toFixed(5)),
            currentPrice: result.position.currentPrice,
            openTime: new Date(result.position.openedAt).getTime(),
            time: new Date(result.position.openedAt).toLocaleTimeString(),
            date: new Date(result.position.openedAt).toLocaleDateString(),
            profit: result.position.unrealizedPnL || 0,
            swap: result.position.swap || 0,
            commission: -Math.abs(result.position.commission || 0),
            stopLoss: 0,
            takeProfit: 0,
            lastUpdate: Date.now(),
            isBackendSynced: true
          };

          setPositions(prev => [...prev, backendPosition]);
          fetchTradingAccountData();
          setStopLoss('');
          setTakeProfit('');
          alert(`${type} order placed successfully! ${selectedSymbol} ${selectedVolume}`);
          return;
        }
      }
      
      console.log('🔄 Backend failed, creating position locally...');
      createLocalPosition(type);
      
    } catch (error) {
      console.error('❌ Error opening position:', error);
      console.log('🔄 Backend error, creating position locally...');
      createLocalPosition(type);
    }
  };

  // ✅ HELPER: Create local position fallback
  const createLocalPosition = (type) => {
    const symbolData = liveQuotes[selectedSymbol];
    if (!symbolData) {
      alert(`Symbol ${selectedSymbol} not available`);
      return;
    }

    const openPrice = type === 'BUY' ? symbolData.ask : symbolData.bid;
    const currentPrice = type === 'BUY' ? symbolData.bid : symbolData.ask;
    const commission = calculateCommission(selectedSymbol, selectedVolume);
    const openTime = Date.now();

    const parsedStopLoss = stopLoss && String(stopLoss).trim() !== '' && !isNaN(parseFloat(stopLoss)) ? parseFloat(stopLoss) : 0;
    const parsedTakeProfit = takeProfit && String(takeProfit).trim() !== '' && !isNaN(parseFloat(takeProfit)) ? parseFloat(takeProfit) : 0;

    const newOrder = {
      id: `${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      positionId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      symbol: String(selectedSymbol),
      type: String(type),
      time: String(new Date().toLocaleTimeString()),
      date: String(new Date().toLocaleDateString()),
      volume: Number(selectedVolume),
     openPrice: parseFloat(parseFloat(openPrice).toFixed(5)),
      currentPrice: Number(currentPrice),
      openTime: Number(openTime),
      profit: Number(0),
      swap: Number(0),
      commission: Number(-commission),
      stopLoss: Number(parsedStopLoss),
      takeProfit: Number(parsedTakeProfit),
      lastUpdate: Number(Date.now())
    };

    setPositions(prev => [...prev, newOrder]);
    
    // Update account balance with commission
    setActiveTradingAccount(prev => ({
      ...prev,
      balance: prev.balance - commission,
      equity: prev.equity - commission
    }));

    setStopLoss('');
    setTakeProfit('');

    const slText = parsedStopLoss > 0 ? ` | Loss: ${parsedStopLoss.toFixed(symbolData?.digits || 5)}` : '';
    const tpText = parsedTakeProfit > 0 ? ` | Profit: ${parsedTakeProfit.toFixed(symbolData?.digits || 5)}` : '';
    alert(`${type} order placed locally! ${selectedSymbol} ${selectedVolume} | Price: ${formatPrice(openPrice, symbolData?.digits || 5)} | Commission: -$${commission.toFixed(2)}${slText}${tpText}`);
  };

  // ✅ UPDATED: Close position on backend with trade history saving
  const closePositionOnBackend = async (positionId, closeReason = 'Manual') => {
    try {
      console.log('🔒 Closing position on backend:', positionId);
      
      const response = await fetch(`${API_BASE_URL}/api/trading/close-position/${positionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders() // ✅ FIXED: Add auth headers
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('✅ Position closed on backend:', result.realizedPnL);
          
          const closedPosition = positions.find(p => p.id === positionId || p.positionId === positionId);
          if (closedPosition) {
  // ✅ Backend saves automatically - just update local display
console.log('✅ Backend saved trade history automatically');
// Load fresh history to display
// Remove from positions immediately
setPositions(prev => prev.filter(p => p.id !== positionId && p.positionId !== positionId));
await fetchTradingAccountData();
// Delay so backend finishes writing trade history before we fetch it
setTimeout(async () => {
      await loadBackendTradeHistory();
    setActiveTerminalTab('account-history');
}, 800);
alert(`Position closed! P&L: ${result.realizedPnL.toFixed(2)}`);
return;
          }
        }
      }
      
      console.log('🔄 Backend failed, closing locally...');
      handleLocalClosePosition(positionId, closeReason);
      
    } catch (error) {
      console.error('❌ Error closing position:', error);
      handleLocalClosePosition(positionId, closeReason);
    }
  };

  // ✅ NEW: Save trade to backend tradeHistory for admin editing
  const saveTradeHistoryToBackend = async (tradeData) => {
    try {
      console.log('💾 Saving trade history to backend:', tradeData.tradeId);
      
      const response = await fetch(`${API_BASE_URL}/api/trading/save-trade-history`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          accountId: activeTradingAccount.accountId,
          tradeData: tradeData
        })
      });

      if (response.ok) {
        console.log('✅ Trade history saved to backend for admin access');
      } else {
        console.error('❌ Failed to save trade history to backend');
      }
    } catch (error) {
      console.error('❌ Error saving trade history to backend:', error);
    }
  };

  // ✅ NEW: Load backend trade history
  // ✅ NEW: Load backend trade history
const loadBackendTradeHistory = async () => {
  if (!activeTradingAccount?.accountId || !user?.id) return;

  try {
    console.log('Loading trade history from backend...');
    // ✅ FIXED: Use accountId-specific endpoint instead of user-wide
    const response = await fetch(`${API_BASE_URL}/api/trading/get-trade-history/${activeTradingAccount.accountId}`, {
      headers: getAuthHeaders()
    });


    if (response.ok) {
      const result = await response.json();
      if (result.success && result.tradeHistory) {
        // Convert backend trade history to frontend format
        const formattedHistory = result.tradeHistory.map(trade => ({
          id: trade._id,
          tradeId: trade.tradeId,
          symbol: trade.symbol,
          type: trade.type.toUpperCase(),
          volume: parseFloat(trade.volume),
          openPrice: parseFloat(trade.openPrice),
          closePrice: parseFloat(trade.closePrice),
          profit: parseFloat(trade.profit), // ✅ This will include admin edits
          commission: parseFloat(trade.commission), // ✅ This will include admin edits
          swap: parseFloat(trade.swap), // ✅ This will include admin edits
          stopLoss: parseFloat(trade.stopLoss || 0),
          takeProfit: parseFloat(trade.takeProfit || 0),
          openTime: trade.openTime,
          closeTime: trade.closeTime,
          date: new Date(trade.closeTime).toLocaleDateString(),
          closedAt: new Date(trade.closeTime).toLocaleTimeString(),
          closedDate: new Date(trade.closeTime).toLocaleDateString(),
          duration: trade.duration || 0,
          closeReason: trade.closeReason || 'Manual',
          adminEdited: trade.adminEdited || false, // ✅ Track if admin edited
          finalProfit: parseFloat(trade.profit) // ✅ Use admin-edited profit
        }));

// ✅ Backend is source of truth — admin deletes/edits are respected
        setTradeHistory(formattedHistory);
        // ✅ Overwrite localStorage so ghost trades never come back on refresh
        localStorage.setItem(
          `tradeHistory_${activeTradingAccount.accountId}`,
          JSON.stringify(formattedHistory)
        );
        console.log(`📊 Loaded ${formattedHistory.length} trade history records from backend`);
        // ✅ Full replace — deletions and edits both reflected immediately
        setPersistentHistory(
          [...formattedHistory].sort((a, b) => new Date(b.closeTime) - new Date(a.closeTime))
        );
      }
    } else {
      console.log('⚠️ Failed to fetch trade history from backend');
      // Fall back to local storage if needed
      const localHistory = JSON.parse(localStorage.getItem(`tradeHistory_${activeTradingAccount.accountId}`) || '[]');
      setTradeHistory(localHistory);
    }
  } catch (error) {
    console.error('❌ Error fetching trade history:', error);
    // Fall back to local storage
    const localHistory = JSON.parse(localStorage.getItem(`tradeHistory_${activeTradingAccount.accountId}`) || '[]');
    setTradeHistory(localHistory);
  }
};

  // ✅ HELPER: Handle local position close fallback
  const handleLocalClosePosition = (positionId, closeReason) => {
    setPositions(currentPositions => {
      const position = currentPositions.find(p => p.id === positionId || p.positionId === positionId);
      if (!position) {
        console.warn('Position not found:', positionId);
        return currentPositions;
      }

      const finalProfit = (position.profit || 0) + (position.swap || 0) + (position.commission || 0);
console.log('🔢 PROFIT CALCULATION:', {
  positionProfit: position.profit,
  swap: position.swap, 
  commission: position.commission,
  finalProfit: finalProfit
});

      const closeTime = Date.now();
      const durationMs = closeTime - (position.openTime || Date.now());
      const durationMinutes = Math.floor(durationMs / (1000 * 60));

      const completedTrade = {
        ...position,
        tradeId: `TH-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        closePrice: position.currentPrice || position.openPrice,
        closeTime: closeTime,
        closedAt: new Date().toLocaleTimeString(),
        closedDate: new Date().toLocaleDateString(),
        openTime: new Date(position.openTime || Date.now()).toLocaleString(),
        finalProfit: finalProfit,
        duration: Math.max(durationMinutes, 0),
        status: 'Closed',
        closeReason: closeReason
      };

      setTradeHistory(prev => {
  const newHistory = [completedTrade, ...prev];
  
  // ✅ SAVE TO LOCALSTORAGE FOR PERSISTENCE
  if (activeTradingAccount?.accountId) {
    localStorage.setItem(`tradeHistory_${activeTradingAccount.accountId}`, JSON.stringify(newHistory));
    console.log('💾 Trade history saved to localStorage:', newHistory.length, 'trades');
  }
  
  return newHistory;
});

      // ✅ CRITICAL FIX: Update balance AND equity when trade closes
      setActiveTradingAccount(prev => {
        const newBalance = prev.balance + finalProfit;
        return {
          ...prev,
          balance: newBalance,
          equity: newBalance + currentPositions.filter(p => p.id !== positionId && p.positionId !== positionId).reduce((sum, pos) => sum + (pos.profit || 0), 0)
        };
      });

      console.log('✅ Position closed locally: P&L =', `$${finalProfit.toFixed(2)}`, `(${closeReason})`);
      
      if (closeReason === 'Manual') {
        alert(`Position closed! P&L: $${finalProfit.toFixed(2)} (${Math.max(durationMinutes, 0)} minutes)`);
      } else {
        alert(`${closeReason} Hit! closed automatically | P&L: $${finalProfit.toFixed(2)} (${Math.max(durationMinutes, 0)} minutes)`);
      }

      return currentPositions.filter(p => p.id !== positionId && p.positionId !== positionId);
    });
  };

 const handleClosePosition = useCallback((positionId, closeReason = 'Manual') => {
  if (user?.id && activeTradingAccount?.accountId) {
    // ✅ FIXED: Use position.positionId from backend instead of position.id
    const position = positions.find(p => p.id === positionId || p.positionId === positionId);
    if (position) {
      closePositionOnBackend(position.positionId || position.id, closeReason);
      return;
    }
  }
  // Fallback to local
  handleLocalClosePosition(positionId, closeReason);
}, [user, activeTradingAccount, positions]);


  // ✅ ENHANCED: Real-time position updates with proper equity calculation
useEffect(() => {
  const interval = setInterval(() => {
    setPositions(prevPositions => {
      const updatedPositions = prevPositions.map(position => {
        const currentQuote = liveQuotes[position.symbol];
        if (!currentQuote) return position;

        const currentPrice = position.type === 'BUY' ? currentQuote.bid : currentQuote.ask;
        const hoursHeld = (Date.now() - (position.openTime || Date.now())) / (1000 * 60 * 60);

        // ✅ CRITICAL: Update backend with new price
        if (position.positionId && position.isBackendSynced) {
          updatePositionOnBackend(position.positionId, currentPrice);
        }

        // Check for SL/TP execution
        let shouldClose = false;
        let closeReason = '';

        if (position.stopLoss > 0) {
          if ((position.type === 'BUY' && currentPrice <= position.stopLoss) ||
              (position.type === 'SELL' && currentPrice >= position.stopLoss)) {
            shouldClose = true;
            closeReason = 'Stop Loss';
          }
        }

        if (position.takeProfit > 0 && !shouldClose) {
          if ((position.type === 'BUY' && currentPrice >= position.takeProfit) ||
              (position.type === 'SELL' && currentPrice <= position.takeProfit)) {
            shouldClose = true;
            closeReason = 'Take Profit';
          }
        }

        if (shouldClose) {
          setTimeout(() => handleClosePosition(position.id || position.positionId, closeReason), 100);
          return position;
        }

        const profit = calculateProfit(position.openPrice || 0, currentPrice, position.volume || 0, position.type === 'BUY', position.symbol);
        const swap = calculateSwap(position.symbol, position.volume || 0, position.type === 'BUY', Math.max(hoursHeld, 0));

        return {
          ...position,
          currentPrice: Number(currentPrice),
          profit: Number(profit),
          swap: Number(swap),
          lastUpdate: Date.now()
        };
      });

        // ✅ ENHANCED: Update equity and auto-calculate margin based on open positions
if (activeTradingAccount) {
  const totalUnrealizedPnL = updatedPositions.reduce((sum, pos) => sum + (pos.profit || 0), 0);
  
// ✅ CORRECTED: Get lot size for margin calculation
const getMarginLotSize = (symbol) => {
  const sym = symbol.toUpperCase();
  
  // ✅ Crypto: 1 lot = 1 coin
  if (sym.includes('BTC') || sym.includes('ETH') || sym.includes('LTC') || 
      sym.includes('ADA') || sym.includes('XRP') || sym.includes('DOT')) {
    return 1;
  }
  
  // ✅ Gold/Metals: 1 lot = 100 troy ounces
  if (sym.includes('XAU') || sym.includes('GOLD')) {
    return 100;
  }
  
  if (sym.includes('XAG') || sym.includes('SILVER')) {
    return 5000; // 5000 ounces per lot
  }
  
  // ✅ Oil/Energy: 1 lot = 1000 barrels
  if (sym.includes('OIL') || sym.includes('WTI') || sym.includes('BRENT')) {
    return 1000;
  }
  
  // ✅ Stock Indices: 1 lot = $1 per point (mini contracts)
  if (sym.includes('US500') || sym.includes('SPX')) {
    return 50; // $50 per point
  }
  if (sym.includes('NAS100') || sym.includes('NDX')) {
    return 20; // $20 per point
  }
  if (sym.includes('US30') || sym.includes('DJI')) {
    return 5; // $5 per point
  }
  
  // ✅ Individual Stocks: 1 lot = 100 shares
  if (sym.includes('AAPL') || sym.includes('GOOGL') || sym.includes('TSLA') || 
      sym.includes('AMZN') || sym.includes('MSFT')) {
    return 100; // Standard stock lot
  }
  
  // ✅ Forex: 1 lot = 100,000 units
  return 100000;
};


const accountLeverage = activeTradingAccount?.leverage || 100; // ✅ DEFINE IT FIRST!
let totalMargin = 0; // ✅ ALSO DEFINE totalMargin

updatedPositions.forEach(position => {
  const lotSize = getMarginLotSize(position.symbol);
  const notionalValue = position.volume * lotSize * position.openPrice;
  const positionMargin = notionalValue / accountLeverage; // ✅ NOW IT WORKS!
  totalMargin += positionMargin;

  
  console.log(`📊 ${position.symbol} margin:`, {
    volume: position.volume,
    lotSize: lotSize,
    price: position.openPrice,
    notionalValue: notionalValue.toFixed(2),
    margin: positionMargin.toFixed(2)
  });
});


const newEquity = activeTradingAccount.balance + (activeTradingAccount.credit || 0) + totalUnrealizedPnL;
const newFreeMargin = newEquity - totalMargin;
setActiveTradingAccount(prev => ({
  ...prev,
  equity: newEquity,
  margin: totalMargin || 0,
  // ONLY override freeMargin from interval when there are open positions.
  // When no positions, admin-set freeMargin from backend must be preserved.
  ...(updatedPositions.length > 0 ? { freeMargin: newFreeMargin } : {})
}));
  
 console.log('🔢 Auto-calculated margin:', totalMargin.toFixed(2), 'from', updatedPositions.length, 'positions at', accountLeverage + 'x leverage');

  // ✅ FIXED: Margin call with cooldown to prevent spam
  if (totalMargin > 0 && newEquity > 0) {  // ✅ Only trigger if equity is positive
    const marginLevel = (newEquity / totalMargin) * 100;
    if (marginLevel < 20 && updatedPositions.length > 0) {
      // ✅ Check cooldown - only fire margin call once every 30 seconds
      const lastMarginCall = window._lastMarginCallTime || 0;
      const now = Date.now();
      if (now - lastMarginCall > 30000) {
        window._lastMarginCallTime = now;
        console.warn('⚠️ MARGIN CALL! Margin level:', marginLevel.toFixed(2) + '%');
        updatedPositions.forEach(pos => {
          setTimeout(() => handleClosePosition(pos.id || pos.positionId, 'Margin Call'), 200);
        });
        alert(`⚠️ MARGIN CALL!\nMargin Level dropped to ${marginLevel.toFixed(2)}%\nAll positions are being closed to protect your account.`);
      }
    }
  }
}

        return updatedPositions;
      });
    }, 2000);

    return () => clearInterval(interval);
 }, [liveQuotes, handleClosePosition, activeTradingAccount]);

  // ✅ NEW: Fetch admin alerts on load and every 30 seconds
  useEffect(() => {
    if (activeTradingAccount?.accountId) {
     fetchAdminAlerts();
    const alertInterval = setInterval(fetchAdminAlerts, 10000);
    return () => clearInterval(alertInterval);
    }
  }, [activeTradingAccount?.accountId]);

 // ✅ NEW: Fetch admin alerts and credit from backend
 const fetchAdminAlerts = async () => {
  if (!activeTradingAccount?.accountId) return;
  if (!isMountedRef.current) return; // ✅ Stop if unmounted
  try {
      const response = await fetch(
        `${API_BASE_URL}/api/trading/alerts/${activeTradingAccount.accountId}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && isMountedRef.current) { // ✅ Guard all setState
          setAdminAlerts(data.alerts || []);

          const credit = data.credit || 0;
          setAccountCredit(credit);

          // ✅ Update credit in activeTradingAccount so equity recalculates
          // This handles admin removing credit while trade is running
          setActiveTradingAccount(prev => {
            if (!prev) return prev;
            const balance = prev.balance || 0;
            const floatingPnL = positions.reduce((sum, p) => sum + (p.profit || 0), 0);
            const newEquity = balance + credit + floatingPnL;
            const newFreeMargin = newEquity - (prev.margin || 0);
            return {
              ...prev,
              credit,
              equity: newEquity,
              freeMargin: newFreeMargin
            };
          });

          setUnreadAlertCount((data.alerts || []).filter(a => !a.isRead).length);
        }
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  // ✅ NEW: Modify SL/TP for a running position
  const handleModifySLTP = async (positionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/trading/modify-sltp/${positionId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          stopLoss: parseFloat(editSLValue) || 0,
          takeProfit: parseFloat(editTPValue) || 0
        })
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPositions(prev => prev.map(p =>
            (p.id === positionId || p.positionId === positionId)
              ? { ...p, stopLoss: parseFloat(editSLValue) || 0, takeProfit: parseFloat(editTPValue) || 0 }
              : p
          ));
          setEditingSLTPId(null);
          setEditSLValue('');
          setEditTPValue('');
          alert('✅ SL/TP modified successfully!');
        }
      }
    } catch (error) {
      console.error('Error modifying SL/TP:', error);
      // Fallback: update locally
      setPositions(prev => prev.map(p =>
        (p.id === positionId || p.positionId === positionId)
          ? { ...p, stopLoss: parseFloat(editSLValue) || 0, takeProfit: parseFloat(editTPValue) || 0 }
          : p
      ));
      setEditingSLTPId(null);
      alert('✅ SL/TP updated!');
    }
  };

 // ✅ FIXED: Proper backend integration
  const handleNewOrder = (orderType) => {
    console.log('🔥 START handleNewOrder:', { orderType, selectedSymbol, selectedVolume, stopLoss, takeProfit });

    // ✅ NEW: Block trading on weekends (Saturday=6, Sunday=0)
    const nowDay = new Date().getDay();
    if (nowDay === 0 || nowDay === 6) {
      alert('⛔ Market is closed on weekends (Saturday & Sunday).\nTrading resumes on Monday.');
      return;
    }

    const symbolData = liveQuotes[selectedSymbol];
    if (!symbolData) {
      alert(`Symbol ${selectedSymbol} not available`);
      return;
    }

    // ✅ NEW: Block if balance is zero or negative
    const currentBalance = activeTradingAccount?.balance || 0;
    if (currentBalance <= 0) {
      alert('⛔ Insufficient balance. Please deposit funds before placing trades.');
      return;
    }

    // ✅ NEW: Block if free margin is zero or negative
    const currentFreeMargin = activeTradingAccount?.freeMargin || activeTradingAccount?.equity || 0;
    if (currentFreeMargin <= 0) {
      alert('⛔ Free margin is zero or negative. Cannot place new orders.\nPlease close existing positions or contact support.');
      return;
    }

    // ✅ NEW: Calculate required margin and block if insufficient
    const getReqLotSize = (sym) => {
      const s = sym.toUpperCase();
      if (s.includes('BTC') || s.includes('ETH') || s.includes('LTC') || s.includes('ADA') || s.includes('XRP')) return 1;
      if (s.includes('XAU') || s.includes('GOLD')) return 100;
      if (s.includes('XAG') || s.includes('SILVER')) return 5000;
      if (s.includes('OIL') || s.includes('WTI') || s.includes('BRENT')) return 1000;
      if (s.includes('US500') || s.includes('SPX')) return 50;
      if (s.includes('NAS100') || s.includes('NDX')) return 20;
      if (s.includes('US30') || s.includes('DJI')) return 5;
      if (s.includes('AAPL') || s.includes('GOOGL') || s.includes('TSLA') || s.includes('AMZN') || s.includes('MSFT')) return 100;
      return 100000;
    };
    const orderPrice = orderType === 'BUY' ? (symbolData.ask || symbolData.bid) : (symbolData.bid || symbolData.ask);
    const accountLev = activeTradingAccount?.leverage || 100;
    const requiredMargin = (parseFloat(selectedVolume) * getReqLotSize(selectedSymbol) * orderPrice) / accountLev;
    if (currentFreeMargin < requiredMargin) {
      alert(`⛔ Insufficient free margin.\nRequired: $${requiredMargin.toFixed(2)}\nAvailable: $${currentFreeMargin.toFixed(2)}\n\nReduce volume or close existing positions.`);
      return;
    }

    // ✅ TRY BACKEND FIRST
    if (user?.id && activeTradingAccount?.accountId) {
      openPositionOnBackend(orderType);
      return;
    }

    // ✅ FALLBACK TO FRONTEND ONLY IF NO BACKEND
    createLocalPosition(orderType);
  };

  // ✅ Your existing initialization
  useEffect(() => {
    console.log('🚀 Initializing MetaTrader Platform...');
    
    const storedAccount = localStorage.getItem('activeTradingAccount');
    if (storedAccount) {
      try {
        const accountData = JSON.parse(storedAccount);
        setActiveTradingAccount(accountData);
        console.log('✅ Account loaded:', accountData.accountId);
      } catch (error) {
        console.error('❌ Account loading error:', error);
        navigate('/metatrader-login');
        return;
      }
    } else {
      console.warn('❌ No stored account, redirecting to login');
      navigate('/metatrader-login');
      return;
    }

    const initializeAllAssets = async () => {
      const assetList = realTimeDataService.getCompleteAssetList();
      const initialQuotes = {};
      
      console.log('📊 Loading', Object.keys(assetList).length, 'symbols...');

      const prioritySymbols = ['XRPUSD', 'XAUUSD', 'EURUSD', 'GBPUSD', 'BTCUSD', 'AAPL', 'GOOGL', 'TSLA', 'US500'];
      
      for (const symbol of prioritySymbols) {
        if (assetList[symbol]) {
          try {
            const realTimeData = await realTimeDataService.fetchRealTimePrice(symbol);
            initialQuotes[symbol] = {
              ...realTimeData,
              ...assetList[symbol],
              lastUpdate: Date.now()
            };
          } catch (error) {
            const fallbackData = realTimeDataService.generateRealisticPrice(symbol, assetList[symbol]);
            initialQuotes[symbol] = {
              ...fallbackData,
              ...assetList[symbol],
              lastUpdate: Date.now()
            };
          }
        }
      }

      Object.entries(assetList).forEach(([symbol, info]) => {
        if (!initialQuotes[symbol]) {
          const fallbackData = realTimeDataService.generateRealisticPrice(symbol, info);
          initialQuotes[symbol] = {
            ...fallbackData,
            ...info,
            lastUpdate: Date.now()
          };
        }
      });

      setLiveQuotes(initialQuotes);
      console.log('📊 All', Object.keys(initialQuotes).length, 'symbols loaded!');
    };

    initializeAllAssets();


    // Load persistent trade history
    const loadTradeHistory = () => {
      if (activeTradingAccount?.accountId) {
        const savedHistory = JSON.parse(localStorage.getItem(`tradeHistory_${activeTradingAccount.accountId}`) || '[]');
        setPersistentHistory(savedHistory);
        setTradeHistory(savedHistory);
        console.log('✅ Loaded', savedHistory.length, 'trades from storage');
      }
    };

    loadTradeHistory();



    const handlePriceUpdate = async (symbol, priceData) => {
      setLiveQuotes(prev => ({
        ...prev,
        [symbol]: {
          ...prev[symbol],
          ...priceData,
          lastUpdate: Date.now()
        }
      }));
    };

    realTimeDataService.subscribe(handlePriceUpdate);

    const refreshInterval = setInterval(async () => {
      const prioritySymbols = ['XRPUSD', 'XAUUSD', 'EURUSD', 'BTCUSD', 'AAPL', 'TSLA'];
      const randomSymbol = prioritySymbols[Math.floor(Math.random() * prioritySymbols.length)];
      
      try {
        const updatedData = await realTimeDataService.fetchRealTimePrice(randomSymbol);
        handlePriceUpdate(randomSymbol, updatedData);
      } catch (error) {
        console.warn('⚠️ Periodic refresh failed for', randomSymbol);
      }
    }, 30000);

    return () => {
    console.log('🧹 Cleaning up MetaTrader Platform');
    isMountedRef.current = false; // ✅ Mark unmounted FIRST
    clearInterval(refreshInterval);
    realTimeDataService.unsubscribe(handlePriceUpdate);
    realTimeDataService.stopRealTimeUpdates();
  };
}, [navigate]);

// ✅ Algo polling — isolated, fires when accountId is available
useEffect(() => {
  if (!activeTradingAccount?.accountId) return;

  const fetchAlgoStatus = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/public/algo-status/${activeTradingAccount.accountId}`);
      const d = await r.json();
      if (d.success !== undefined) {
        setAlgoTradingActive(d.algoTradingActive || false);
      }
    } catch (_) {}
  };

  fetchAlgoStatus();                                    // immediate on mount
  const interval = setInterval(fetchAlgoStatus, 10000); // poll every 10s
  return () => clearInterval(interval);                 // clean up
}, [activeTradingAccount?.accountId]);

// ✅ FIXED: Only run ONCE when account first loads — not on every update
 const hasInitializedRef = useRef(false);
const isMountedRef = useRef(true); // ✅ ADD: track mount state

  useEffect(() => {
    // ✅ Only run once per account login, not on every state change
    if (user?.id && activeTradingAccount?.accountId && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      console.log('📡 Account ready - loading backend data ONCE for:', activeTradingAccount.accountId);
      
      setTimeout(() => {
        loadBackendTradeHistory();
      }, 500);
      
      setTimeout(async () => {
        await syncPositionsFromBackend();
        console.log('✅ Positions synced from backend');
      }, 1000);
    }

    // ✅ Poll every 15s — catches admin force-closes without needing page refresh
    const adminForcePollInterval = setInterval(async () => {
      if (activeTradingAccount?.accountId) {
        await syncPositionsFromBackend();
      }
    }, 15000);

    return () => clearInterval(adminForcePollInterval);
  }, [activeTradingAccount?.accountId]); // ✅ Only re-run if accountId changes (actual login switch)

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // ✅ Your existing categories
  const categories = {
    'Most Popular': ['XRPUSD', 'XAUUSD', 'EURUSD', 'GBPUSD', 'BTCUSD', 'US500', 'WTI', 'AAPL', 'GOOGL', 'TSLA', 'ETHUSD'],
    'Forex': ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD'],
    'Forex Cross': ['EURGBP', 'EURJPY', 'GBPJPY', 'EURCHF', 'GBPCHF', 'AUDCHF'],
    'Forex Minors': ['EURAUD', 'EURCAD', 'EURNZD', 'GBPAUD', 'GBPCAD', 'CADCHF'],
    'Metals': ['XAUUSD', 'XAGUSD', 'XPDUSD', 'XPTUSD'],
    'Energies': ['WTI', 'BRENT', 'NATGAS', 'HEATING', 'GASOLINE'],
    'Indices': ['US500', 'NAS100', 'US30', 'GER40', 'UK100', 'FRA40', 'JPN225', 'AUS200'],
    'Crypto': ['BTCUSD', 'ETHUSD', 'XRPUSD', 'LTCUSD', 'ADAUSD', 'DOTUSD', 'LINKUSD', 'BNBUSD'],
    'US Stocks': ['AAPL', 'GOOGL', 'MSFT', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX', 'BABA', 'AMD']
  };

  // Helper functions
  const getDisplaySymbols = () => {
    if (searchTerm.trim()) {
      return Object.keys(liveQuotes).filter(symbol => 
        symbol.toLowerCase().includes(searchTerm.toLowerCase()) || 
        liveQuotes[symbol].name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return categories[selectedCategory];
  };

  const displaySymbols = getDisplaySymbols();

  const formatPrice = (price, digits = 5) => {
    if (!price || isNaN(price)) return '0.00000';
    return Number(price).toFixed(digits);
  };

  const getSpreadPips = (bid, ask, symbol) => {
    const spread = ask - bid;
    if (symbol.includes('JPY')) {
      return (spread * 100).toFixed(1);
    }
    return (spread * 10000).toFixed(1);
  };

  const currentSymbolData = liveQuotes[selectedSymbol];
  const currentBidPrice = currentSymbolData ? formatPrice(currentSymbolData.bid, currentSymbolData.digits) : '0.00';
  const currentAskPrice = currentSymbolData ? formatPrice(currentSymbolData.ask, currentSymbolData.digits) : '0.00';

  const handleSymbolChange = (symbol) => {
    console.log('🔄 Symbol changed to:', symbol);
    setSelectedSymbol(symbol);
    
    realTimeDataService.fetchRealTimePrice(symbol).then(priceData => {
      if (priceData) {
        setLiveQuotes(prev => ({
          ...prev,
          [symbol]: {
            ...prev[symbol],
            ...priceData,
            lastUpdate: Date.now()
          }
        }));
      }
    });
  };

  const handleTimeframeChange = (newTimeframe) => {
    console.log('⏰ Timeframe changed to:', newTimeframe);
    setTimeframe(newTimeframe);
  };

  const handleLogout = () => {
    console.log('👋 Logging out...');
    realTimeDataService.stopRealTimeUpdates();
    localStorage.removeItem('activeTradingAccount');
    navigate('/dashboard');
  };

  const timeframes = [
    { mt5: 'M1', tv: '1', label: 'M1' },
    { mt5: 'M5', tv: '5', label: 'M5' },
    { mt5: 'M15', tv: '15', label: 'M15' },
    { mt5: 'M30', tv: '30', label: 'M30' },
    { mt5: 'H1', tv: '60', label: 'H1' },
    { mt5: 'H4', tv: '240', label: 'H4' },
    { mt5: 'D1', tv: 'D', label: 'D1' },
    { mt5: 'W1', tv: 'W', label: 'W1' },
    { mt5: 'MN', tv: 'M', label: 'MN' }
  ];

  const currentTimeframe = timeframes.find(tf => tf.tv === timeframe) || timeframes[4];

  const symbolMapping = getSymbolMapping();
  const tradingViewSymbol = symbolMapping[selectedSymbol] || 'COINBASE:XRPUSD';

  // ✅ CRITICAL: Calculate real-time totals for display
  const currentProfit = positions.reduce((sum, pos) => sum + (pos.profit || 0), 0);
  const currentEquity = activeTradingAccount ? activeTradingAccount.balance + currentProfit : 0;
  const currentFreeMargin = currentEquity - (activeTradingAccount?.margin || 0);

  if (!activeTradingAccount) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div>Loading MetaTrader Platform...</div>
        <div className="loading-subtitle">Initializing trading platform...</div>
      </div>
    );
  }

  return (
    <div className="mt5-platform">
      <div className="status-banner">
        📈 StoneForex Professional Trading Platform | 
        Symbols: {Object.keys(liveQuotes).length} | 
        Server: StoneForex-Live | 
        Status: Connected
      </div>
      <div className="mt5-toolbar">
        <div className="toolbar-left">
          <div className="mt5-logo">
            <span>MT5</span>
          </div>
          
          <div className="toolbar-buttons">
            {timeframes.map(tf => (
              <button 
                key={tf.mt5}
                className={`timeframe-btn ${timeframe === tf.tv ? 'active' : ''}`}
                onClick={() => handleTimeframeChange(tf.tv)}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>

       <div className="toolbar-center">
          {/* ✅ Clean toolbar - metrics shown in Trade tab footer like real MT5 */}
          <div className="account-info">
            <span>Account: <strong>{activeTradingAccount.accountId}</strong></span>
            <span style={{ color: '#aaa', fontSize: '11px' }}>
              {activeTradingAccount?.group || 'STANDARD'}
            </span>
          </div>
        </div>
<div className="toolbar-right">
        {/* ✅ Algo Robot Icon - visible/animated when active, greyed when off */}
<div className={`algo-robot-wrap ${algoTradingActive ? 'algo-on' : 'algo-off'}`}>
  <svg className="algo-robot-svg" viewBox="0 0 24 24" width="22" height="22" fill="none">
    <rect x="7" y="11" width="10" height="9" rx="2" fill={algoTradingActive ? '#22c55e' : '#9ca3af'}/>
    <rect x="9" y="6" width="6" height="6" rx="1" fill={algoTradingActive ? '#16a34a' : '#6b7280'}/>
    <circle cx="10" cy="9" r="1.2" fill={algoTradingActive ? '#fff' : '#d1d5db'}/>
    <circle cx="14" cy="9" r="1.2" fill={algoTradingActive ? '#fff' : '#d1d5db'}/>
    <rect x="11" y="2" width="2" height="4" rx="1" fill={algoTradingActive ? '#22c55e' : '#9ca3af'}/>
    <rect x="4" y="14" width="3" height="4" rx="1" fill={algoTradingActive ? '#22c55e' : '#9ca3af'}/>
    <rect x="17" y="14" width="3" height="4" rx="1" fill={algoTradingActive ? '#22c55e' : '#9ca3af'}/>
    <rect x="9.5" y="15" width="5" height="2" rx="1" fill={algoTradingActive ? '#bbf7d0' : '#e5e7eb'}/>
  </svg>
  <span style={{
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.4px',
    color: algoTradingActive ? '#16a34a' : '#9ca3af',
    transition: 'color 0.3s'
  }}>
    ALGO TRADING
  </span>
</div>
          <div className="connection-status">
            <span className="status-dot connected"></span>
            <span>StoneForex-Live</span>
          </div>
          <div className="server-time">
            {currentTime.toLocaleTimeString()}
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="mt5-content">
        {/* Market Watch Panel */}
        <div className={`market-watch ${isMarketWatchExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="panel-header">
            <span>Market Watch</span>
            <div className="panel-controls">
              <span className="instrument-count">{displaySymbols.length}</span>
              <button 
                className="expand-toggle" 
                onClick={() => setIsMarketWatchExpanded(!isMarketWatchExpanded)}
              >
                {isMarketWatchExpanded ? '◀' : '▶'}
              </button>
            </div>
          </div>

          {isMarketWatchExpanded && (
            <>
              <div className="market-search">
                <input
                  type="text"
                  placeholder="Search symbols..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {!searchTerm && (
                <div className="category-tabs">
                  {Object.keys(categories).map(category => (
                    <button
                      key={category}
                      className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedCategory(category);
                        setSearchTerm('');
                      }}
                    >
                      <span className="category-name">{category}</span>
                      <span className="category-count">{categories[category].length}</span>
                    </button>
                  ))}
                </div>
              )}

              <div className="symbols-list">
                <div className="symbols-header">
                  <span>Symbol</span>
                  <span>Bid</span>
                  <span>Ask</span>
                  <span>Spread</span>
                </div>
                
                <div className="symbols-body">
                  {displaySymbols.map(symbol => {
                    const quote = liveQuotes[symbol];
                    if (!quote) return null;

                    return (
                      <div 
                        key={symbol}
                        className={`symbol-row ${selectedSymbol === symbol ? 'selected' : ''}`}
                        onClick={() => handleSymbolChange(symbol)}
                        onDoubleClick={() => handleNewOrder('BUY')}
                        title={quote.name}
                      >
                        <div className="symbol-info">
                          <span className="symbol-name">{getDisplayName(symbol)}</span>
                        </div>
                        <span className="bid-price">{formatPrice(quote.bid, quote.digits)}</span>
                        <span className="ask-price">{formatPrice(quote.ask, quote.digits)}</span>
                        <span className="spread-pips">{getSpreadPips(quote.bid, quote.ask, symbol)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="market-summary">
                <div className="summary-item">
                  <span>Total Symbols</span>
                  <span>{Object.keys(liveQuotes).length}</span>
                </div>
                <div className="summary-item">
                  <span>Server</span>
                  <span className="status-online">Connected</span>
                </div>
                <div className="summary-item">
                  <span>Last Update</span>
                  <span>{currentTime.toLocaleTimeString()}</span>
                </div>
              </div>
            </>
          )}

          {!isMarketWatchExpanded && (
            <button className="expand-market-watch-btn" onClick={() => setIsMarketWatchExpanded(true)}>
              ▶
            </button>
          )}
        </div>

        {/* Chart Area */}
        <div className="chart-area">
          <div className="chart-header">
            <div className="chart-info">
              <h3>{getDisplayName(selectedSymbol)}</h3>
              <span className="chart-timeframe">{currentTimeframe.label}</span>
              <span className="chart-name">{currentSymbolData?.name}</span>
              <span className="tradingview-symbol" style={{ fontSize: '10px', color: '#666' }}>
                {tradingViewSymbol}
              </span>
            </div>
            <div className="chart-controls">
              <button className="buy-btn" onClick={() => handleNewOrder('BUY')}>
                Buy {currentAskPrice}
              </button>
              <button className="sell-btn" onClick={() => handleNewOrder('SELL')}>
                Sell {currentBidPrice}
              </button>
            </div>
          </div>
          
          <div className="chart-container">
            <RealTimeChart symbol={tradingViewSymbol} interval={timeframe} />
          </div>
        </div>

        {/* Trade Terminal */}
        <div className="trade-terminal">
          <div className="panel-header">
            <span>Order</span>
          </div>
          
          <div className="order-form">
            <div className="order-tabs">
              <button className="tab-btn active">Market Order</button>
              <button className="tab-btn">Pending Order</button>
            </div>

            <div className="form-group">
              <label>Symbol</label>
              <select 
                value={selectedSymbol} 
                onChange={(e) => handleSymbolChange(e.target.value)}
              >
                {Object.keys(liveQuotes).map(symbol => (
                  <option key={symbol} value={symbol}>
                    {getDisplayName(symbol)} - {liveQuotes[symbol]?.name}
                  </option>
                ))}
              </select>
            </div>

           <div className="form-group">
              <label>
                Volume
                <span style={{ float: 'right', color: '#999', fontSize: '10px', fontWeight: 400 }}>
                  max {(activeTradingAccount?.maxVolume || 100).toFixed(2)}
                </span>
              </label>
              <input
                type="number"
                min="0.01"
                max={activeTradingAccount?.maxVolume || 100}
                step="0.01"
                value={selectedVolume}
                onChange={(e) => {
                  const raw = e.target.value;
                  setSelectedVolume(raw);
                }}
                onBlur={(e) => {
                  const max = activeTradingAccount?.maxVolume || 100;
                  let v = parseFloat(parseFloat(e.target.value).toFixed(2));
                  if (isNaN(v) || v < 0.01) v = 0.01;
                  if (v > max) v = max;
                  setSelectedVolume(v);
                }}
                style={{ width: '100%' }}
              />
            </div>

            <div className="order-buttons">
              <button className="market-buy" onClick={() => handleNewOrder('BUY')}>
                <div>Buy by Market</div>
                <div className="price">{currentAskPrice}</div>
              </button>
              <button className="market-sell" onClick={() => handleNewOrder('SELL')}>
                <div>Sell by Market</div>
                <div className="price">{currentBidPrice}</div>
              </button>
            </div>

            <div className="form-group">
              <label>Stop Loss</label>
              <input
                type="number"
                step="0.0001"
                placeholder="0.0000"
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Take Profit</label>
              <input
                type="number"
                step="0.0001"
                placeholder="0.0000"
                value={takeProfit}
                onChange={(e) => setTakeProfit(e.target.value)}
              />
            </div>

            <div className="live-price-debug">
              <div><strong>🔥 PROFESSIONAL TRADING</strong></div>
             <div><strong>Symbol:</strong> {getDisplayName(selectedSymbol)}</div>
              <div><strong>Name:</strong> {currentSymbolData?.name}</div>
              <div>Bid: {currentBidPrice} | Ask: {currentAskPrice}</div>
              <div>Volume: {selectedVolume}</div>
              <div>Commission: ${calculateCommission(selectedSymbol, selectedVolume).toFixed(2)}</div>
              <div>SL: {stopLoss || 'None'} | TP: {takeProfit || 'None'}</div>
              <div>TradingView: {tradingViewSymbol}</div>
              <div>Open Positions: {positions.length}</div>
              <div>Trade History: {tradeHistory.length}</div>
              <div>Last Update: {currentSymbolData ? new Date(currentSymbolData.lastUpdate).toLocaleTimeString() : 'N/A'}</div>
              <div className="status-online">Professional Trading Platform - StoneForex + TradingView</div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ NEW: Bottom terminal - Real MetaTrader Style */}
      <div className="bottom-terminal">
        <div className="terminal-tabs">
          <button
            className={`tab-btn ${activeTerminalTab === 'Trade' ? 'active' : ''}`}
            onClick={() => setActiveTerminalTab('Trade')}
          >
            Trade ({positions.length})
          </button>
          <button
            className={`tab-btn ${activeTerminalTab === 'account-history' ? 'active' : ''}`}
            onClick={() => {
              setActiveTerminalTab('account-history');
              if (activeTradingAccount?.accountId) loadBackendTradeHistory();
            }}
          >
            Account History ({(tradeHistory || []).length})
          </button>
          <button
            className={`tab-btn ${activeTerminalTab === 'News' ? 'active' : ''}`}
            onClick={() => setActiveTerminalTab('News')}
          >
            News
          </button>
          <button
            className={`tab-btn ${activeTerminalTab === 'Alerts' ? 'active' : ''}`}
            onClick={() => { setActiveTerminalTab('Alerts'); fetchAdminAlerts(); }}
          >
            Alerts
            {unreadAlertCount > 0 && (
              <span style={{ background: '#f44336', color: 'white', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', marginLeft: '4px' }}>
                {unreadAlertCount}
              </span>
            )}
          </button>
        </div>

        <div className="trade-panel" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* ===== TRADE TAB ===== */}
          {activeTerminalTab === 'Trade' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* Positions table */}
              <div className="positions-section" style={{ flex: 1, overflow: 'auto' }}>
                <table className="positions-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 5 }}>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Position</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Time</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Volume</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Symbol</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Open Price</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>S/L</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>T/P</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Current Price</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Commission</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Swap</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Floating P&L</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.length === 0 ? (
                      <tr>
                        <td colSpan="13" style={{ padding: '20px', textAlign: 'center', color: '#666', border: '1px solid #ddd' }}>
                          No open positions — Place a BUY or SELL order to see it here
                        </td>
                      </tr>
                    ) : (
                      positions.map((position, index) => {
                        const isEditing = editingSLTPId === (position.id || position.positionId);
                        return (
                          <tr key={position.id || position.positionId || index}
                              style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9' }}>
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px', fontWeight: 'bold', color: '#1976d2' }}>
                              #{String(position.positionId || position.id || '').slice(-6)}
                            </td>
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px' }}>
                              {position.time || new Date(position.openTime || Date.now()).toLocaleTimeString()}
                            </td>
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px', fontWeight: 'bold',
                                color: (position.type || '').toUpperCase() === 'BUY' ? '#4caf50' : '#f44336' }}>
                              {(position.type || '').toUpperCase()}
                            </td>
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px' }}>
                              {Number(position.volume || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px', fontWeight: 'bold' }}>
                              {position.symbol}
                            </td>
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px', fontFamily: 'monospace' }}>
                              {Number(position.openPrice || 0).toFixed(5)}
                            </td>

                            {/* S/L with inline edit */}
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px' }}>
                              {isEditing ? (
                                <input type="number" step="0.0001" value={editSLValue}
                                  onChange={e => setEditSLValue(e.target.value)}
                                  style={{ width: '80px', padding: '2px 4px', fontSize: '11px', border: '1px solid #1976d2', borderRadius: '3px' }} />
                              ) : (
                                <span style={{ color: '#f44336' }}>
                                  {position.stopLoss > 0 ? Number(position.stopLoss).toFixed(5) : '0.00000'}
                                </span>
                              )}
                            </td>

                            {/* T/P with inline edit */}
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px' }}>
                              {isEditing ? (
                                <input type="number" step="0.0001" value={editTPValue}
                                  onChange={e => setEditTPValue(e.target.value)}
                                  style={{ width: '80px', padding: '2px 4px', fontSize: '11px', border: '1px solid #1976d2', borderRadius: '3px' }} />
                              ) : (
                                <span style={{ color: '#4caf50' }}>
                                  {position.takeProfit > 0 ? Number(position.takeProfit).toFixed(5) : '0.00000'}
                                </span>
                              )}
                            </td>

                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px', fontFamily: 'monospace' }}>
                              {Number(position.currentPrice || 0).toFixed(5)}
                            </td>
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px', color: '#f44336' }}>
                              {Number(position.commission || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px' }}>
                              {Number(position.swap || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px', fontWeight: 'bold',
                                color: (position.profit || 0) >= 0 ? '#4caf50' : '#f44336' }}>
                              {Number(position.profit || 0) >= 0 ? '+' : ''}{Number(position.profit || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: '6px 8px', border: '1px solid #eee', fontSize: '11px' }}>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {isEditing ? (
                                  <>
                                    <button onClick={() => handleModifySLTP(position.id || position.positionId)}
                                      style={{ background: '#4caf50', color: 'white', border: 'none', padding: '3px 7px', borderRadius: '3px', cursor: 'pointer', fontSize: '10px' }}>
                                      ✓ Save
                                    </button>
                                    <button onClick={() => { setEditingSLTPId(null); setEditSLValue(''); setEditTPValue(''); }}
                                      style={{ background: '#666', color: 'white', border: 'none', padding: '3px 7px', borderRadius: '3px', cursor: 'pointer', fontSize: '10px' }}>
                                      ✕
                                    </button>
                                  </>
                                ) : (
                                  <button onClick={() => {
                                    setEditingSLTPId(position.id || position.positionId);
                                    setEditSLValue(position.stopLoss > 0 ? String(position.stopLoss) : '');
                                    setEditTPValue(position.takeProfit > 0 ? String(position.takeProfit) : '');
                                  }}
                                    style={{ background: '#ff9800', color: 'white', border: 'none', padding: '3px 7px', borderRadius: '3px', cursor: 'pointer', fontSize: '10px' }}>
                                    Edit SL/TP
                                  </button>
                                )}
                                <button onClick={() => handleClosePosition(position.id || position.positionId, 'Manual')}
                                  style={{ background: '#f44336', color: 'white', border: 'none', padding: '3px 7px', borderRadius: '3px', cursor: 'pointer', fontSize: '10px' }}>
                                  Close
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* ✅ NEW: Bottom metrics bar - exactly like real MetaTrader */}
              <div style={{
                display: 'flex', gap: '20px', padding: '5px 12px',
                background: '#1a1a2e', borderTop: '2px solid #333',
                flexShrink: 0, flexWrap: 'wrap', alignItems: 'center'
              }}>
                <span style={{ fontSize: '11px' }}>
                  <span style={{ color: '#aaa' }}>Balance: </span>
                  <span style={{ color: '#fff', fontWeight: 'bold', fontFamily: 'monospace' }}>
                    ${Number(activeTradingAccount?.balance || 0).toFixed(2)}
                  </span>
                </span>
                {positions.length > 0 && (() => {
  const totalFloatingPnL = positions.reduce((sum, p) => sum + (Number(p.profit) || 0), 0);
  return (
    <span style={{ fontSize: '11px' }}>
      <span style={{ color: '#aaa' }}>Profit: </span>
      <span style={{
        fontWeight: 'bold', fontFamily: 'monospace',
        color: totalFloatingPnL >= 0 ? '#4caf50' : '#f44336'
      }}>
        {totalFloatingPnL >= 0 ? '+' : ''}${totalFloatingPnL.toFixed(2)}
      </span>
    </span>
  );
})()}
               <span style={{ fontSize: '11px' }}>
                  <span style={{ color: '#aaa' }}>Equity: </span>
                  <span style={{ fontWeight: 'bold', fontFamily: 'monospace',
                      color: (activeTradingAccount?.equity || 0) >= (activeTradingAccount?.balance || 0) ? '#4caf50' : '#f44336' }}>
                    {/* Equity = Balance + Credit + Floating P&L */}
                    ${Number(
                      (activeTradingAccount?.balance || 0) +
                      (activeTradingAccount?.credit || 0) +
                      positions.reduce((sum, p) => sum + (p.profit || 0), 0)
                    ).toFixed(2)}
                  </span>
                </span>
                <span style={{ fontSize: '11px' }}>
                  <span style={{ color: '#aaa' }}>Free Margin: </span>
                  <span style={{ fontWeight: 'bold', fontFamily: 'monospace',
                                          color: ((activeTradingAccount?.balance || 0) + (activeTradingAccount?.credit || 0) + positions.reduce((sum, p) => sum + (p.profit || 0), 0) - (activeTradingAccount?.margin || 0)) > 0 ? '#2196f3' : '#f44336' }}>
                    ${Number((activeTradingAccount?.balance || 0) + (activeTradingAccount?.credit || 0) + positions.reduce((sum, p) => sum + (p.profit || 0), 0) - (activeTradingAccount?.margin || 0)).toFixed(2)}
                  </span>
                </span>

                {/* Credit - only shown if admin set it above 0 */}
                {accountCredit > 0 && (
                  <span style={{ fontSize: '11px' }}>
                    <span style={{ color: '#aaa' }}>Credit: </span>
                    <span style={{ color: '#9c27b0', fontWeight: 'bold', fontFamily: 'monospace' }}>
                      ${Number(accountCredit).toFixed(2)}
                    </span>
                  </span>
                )}

                {/* Margin + Margin Level - only shown when positions are open */}
                {positions.length > 0 && (
                  <>
                    <span style={{ fontSize: '11px' }}>
                      <span style={{ color: '#aaa' }}>Margin: </span>
                      <span style={{ color: '#ff9800', fontWeight: 'bold', fontFamily: 'monospace' }}>
                        ${Number(activeTradingAccount?.margin || 0).toFixed(2)}
                      </span>
                    </span>
                    {(activeTradingAccount?.margin || 0) > 0 && (
                      <span style={{ fontSize: '11px' }}>
                        <span style={{ color: '#aaa' }}>Margin Level: </span>
                        <span style={{ fontWeight: 'bold', fontFamily: 'monospace',
                            color: (((activeTradingAccount?.equity || 0) / (activeTradingAccount?.margin || 1)) * 100) > 100 ? '#4caf50' : '#f44336' }}>
                          {(((activeTradingAccount?.equity || 0) / (activeTradingAccount?.margin || 1)) * 100).toFixed(2)}%
                        </span>
                      </span>
                    )}
                  </>
                )}

                {/* Account type shown on right side */}
                <span style={{ marginLeft: 'auto', fontSize: '11px' }}>
                  <span style={{ color: '#aaa' }}>Account: </span>
                  <span style={{ color: '#ffd700', fontWeight: 'bold' }}>
                    {activeTradingAccount?.group || 'STANDARD'}
                  </span>
                </span>
              </div>
            </div>
          )}

          {/* ===== ACCOUNT HISTORY TAB ===== */}
          {activeTerminalTab === 'account-history' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div className="history-section" style={{ flex: 1, overflow: 'auto' }}>
                <table className="positions-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                   <tr style={{ backgroundColor: '#1976d2', color: 'white', position: 'sticky', top: 0, zIndex: 5 }}>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>Order</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>Open Time</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>Volume</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>Symbol</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>Open Price</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>S/L</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>T/P</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>Close Time</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>Close Price</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>Balance</th>
                      <th style={{ padding: '10px 8px', border: '1px solid #1565c0', textAlign: 'left' }}>Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradeHistory.length === 0 ? (
                      <tr>
                       <td colSpan="11" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                          No trade history yet — Close some positions to see them here
                        </td>
                      </tr>
                    ) : (
                      tradeHistory.map((trade, index) => (
                        <tr key={`${trade.tradeId || trade.id}-${index}`}
                            style={{ backgroundColor: index % 2 === 0 ? '#f0f8ff' : '#ffffff' }}>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontWeight: 'bold', color: '#1976d2', fontSize: '12px' }}>
                            {String(trade.tradeId || trade.id || 'N/A').slice(-8)}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontSize: '11px' }}>
                            {trade.openTime ? new Date(trade.openTime).toLocaleString() : (trade.date && trade.time) ? `${trade.date} ${trade.time}` : 'N/A'}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontWeight: 'bold', fontSize: '12px',
                              color: (trade.type || '').toUpperCase() === 'BUY' ? '#4caf50' : '#f44336' }}>
                            {(trade.type || 'N/A').toUpperCase()}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontSize: '12px' }}>
                            {Number(trade.volume || 0).toFixed(2)}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontWeight: 'bold', fontSize: '13px', color: '#2e7d32' }}>
                            {trade.symbol || 'N/A'}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontFamily: 'monospace', fontSize: '12px' }}>
                            {Number(trade.openPrice || 0).toFixed(5)}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontSize: '11px', color: '#f44336' }}>
                            {(trade.stopLoss || 0) > 0 ? Number(trade.stopLoss).toFixed(5) : '—'}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontSize: '11px', color: '#4caf50' }}>
                            {(trade.takeProfit || 0) > 0 ? Number(trade.takeProfit).toFixed(5) : '—'}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontSize: '11px' }}>
                            {trade.closeTime ? new Date(trade.closeTime).toLocaleString() : (trade.closedDate && trade.closedAt) ? `${trade.closedDate} ${trade.closedAt}` : 'N/A'}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontFamily: 'monospace', fontSize: '12px' }}>
                            {Number(trade.closePrice || 0).toFixed(5)}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontSize: '11px', color: '#1976d2', fontWeight: 'bold' }}>
                            ${Number(activeTradingAccount?.balance || 0).toFixed(2)}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #bbdefb', fontWeight: 'bold', fontSize: '13px',
                              color: (trade.profit || 0) >= 0 ? '#4caf50' : '#f44336' }}>
                            {Number(trade.profit || 0) >= 0 ? '+' : ''}{Number(trade.profit || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

            {/* ✅ History footer — balance, profit, credit only */}
              {tradeHistory.length > 0 && (() => {
                const totalProfit     = tradeHistory.reduce((sum, t) => sum + (Number(t.profit)     || 0), 0);
                const totalCommission = tradeHistory.reduce((sum, t) => sum + (Number(t.commission) || 0), 0);
                const totalSwap       = tradeHistory.reduce((sum, t) => sum + (Number(t.swap)       || 0), 0);
                const netProfit       = totalProfit + totalCommission + totalSwap;
                const currentBalance  = activeTradingAccount?.balance || 0;
                const currentCredit   = activeTradingAccount?.credit  || 0;
                return (
                  <div style={{
                    display: 'flex', gap: '24px', padding: '5px 12px',
                    background: '#1a1a2e', borderTop: '2px solid #333',
                    flexShrink: 0, flexWrap: 'wrap', alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '11px' }}>
                      <span style={{ color: '#aaa' }}>Total Profit: </span>
                      <span style={{ fontWeight: 'bold', fontFamily: 'monospace',
                          color: netProfit >= 0 ? '#4caf50' : '#f44336' }}>
                        {netProfit >= 0 ? '+' : ''}{netProfit.toFixed(2)}
                      </span>
                    </span>
                    {currentCredit > 0 && (
                      <span style={{ fontSize: '11px' }}>
                        <span style={{ color: '#aaa' }}>Credit: </span>
                        <span style={{ color: '#9c27b0', fontWeight: 'bold', fontFamily: 'monospace' }}>
                          ${currentCredit.toFixed(2)}
                        </span>
                      </span>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: '11px' }}>
                      <span style={{ color: '#aaa' }}>Balance: </span>
                      <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', fontFamily: 'monospace' }}>
                        ${currentBalance.toFixed(2)}
                      </span>
                    </span>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ===== NEWS TAB ===== */}

          {/* ===== NEWS TAB ===== */}
          {activeTerminalTab === 'News' && (
            <div style={{ padding: '20px', color: '#666', textAlign: 'center' }}>
              No news available at this time.
            </div>
          )}

          {/* ===== ALERTS TAB ===== */}
          {activeTerminalTab === 'Alerts' && (
            <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
              {adminAlerts.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  No alerts at this time. Admin messages will appear here.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', width: '160px' }}>Date/Time</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Message</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', width: '80px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...adminAlerts].reverse().map((alert, idx) => (
                      <tr key={alert.alertId || idx}
                          style={{ backgroundColor: alert.isRead ? '#fff' : '#fffde7' }}>
                        <td style={{ padding: '8px', border: '1px solid #eee', color: '#666', fontFamily: 'monospace', fontSize: '11px' }}>
                          {alert.createdAt ? new Date(alert.createdAt).toLocaleString() : 'N/A'}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #eee', color: '#333', fontWeight: alert.isRead ? 'normal' : 'bold' }}>
                          {alert.message}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center' }}>
                          {alert.isRead ? (
                            <span style={{ color: '#999', fontSize: '10px' }}>Read</span>
                          ) : (
                            <button
                              onClick={async () => {
                                try {
                                  await fetch(
                                    `${API_BASE_URL}/api/trading/alerts/${activeTradingAccount?.accountId}/${alert.alertId}/read`,
                                    { method: 'PUT', headers: getAuthHeaders() }
                                  );
                                  fetchAdminAlerts();
                                } catch (e) { console.error(e); }
                              }}
                              style={{ background: '#1976d2', color: 'white', border: 'none', padding: '3px 8px', borderRadius: '3px', cursor: 'pointer', fontSize: '10px' }}>
                              Mark Read
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

        </div>
      </div>
      </div>
  );
};

export default MetaTraderPlatform;
